import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check, FileCheck2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { Capabilities } from "@/sections/Capabilities";
import { CtaBand } from "@/sections/CtaBand";
import { IMG, PRODUCTS } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";

const ProductRow = ({ p, i, s }) => (
  <article id={p.id} className="scroll-mt-32 grid items-center gap-8 border-t border-[var(--line)] py-14 lg:grid-cols-12" data-testid={`service-product-${p.id}`}>
    <Reveal className={`img-zoom overflow-hidden rounded-3xl lg:col-span-5 ${i % 2 ? "lg:order-last" : ""}`}>
      <img src={p.img} alt={s.name} className="aspect-[4/3] w-full object-cover" loading="lazy" />
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-auto">
      <span className="rounded-full px-3 py-1.5 font-mono-label text-[10px] text-white" style={{ background: p.color }}>{s.tag}</span>
      <h3 className="font-display mt-5 text-3xl font-bold leading-tight lg:text-4xl">{s.name}</h3>
      <p className="mt-4 text-base leading-relaxed text-[var(--ink-2)] md:text-lg">{s.blurb}</p>
      <ul className="mt-6 space-y-2.5">
        {s.items.map((it) => (
          <li key={it} className="flex items-center gap-3 text-[15px]"><Check size={16} className="text-[var(--green)]" />{it}</li>
        ))}
      </ul>
    </Reveal>
  </article>
);

export default function Services() {
  const { hash } = useLocation();
  const { t, lang } = useLang();
  const s = t.services;
  useEffect(() => {
    if (!hash) return;
    const tm = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    return () => clearTimeout(tm);
  }, [hash]);

  return (
    <main data-testid="services-page">
      <Seo
        title="Wholesale Peppers, Chilies, Tomatoes & Squash — Sebastian's Produce LA"
        description="Bulk, fresh-cut and IQF produce for food manufacturers and restaurants: colored bell peppers, Mexican chilies, cucumbers, tomatoes, squash, salads and fruit. COAs available on request."
      />
      <PageHero key={lang} eyebrow={s.eyebrow} lines={s.lines} text={s.text} image={IMG.redPeppers} testId="services-hero" />

      <section className="pb-8 lg:pb-16" data-testid="services-products-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeader eyebrow={s.coreEyebrow} title={s.coreTitle} />
          <div className="mt-10">
            {PRODUCTS.map((p, i) => <ProductRow key={p.id} p={p} i={i} s={t.products.items[p.id]} />)}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="services-formats-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeader eyebrow={s.formatsEyebrow} title={s.formatsTitle} />
          <div className="mt-12 grid gap-4 lg:grid-cols-3">
            {s.formats.map((f, i) => (
              <Reveal key={i} delay={i * 0.1} className={`rounded-3xl p-8 ${i === 1 ? "bg-[var(--green)] text-white" : "border border-[var(--line)] bg-white"}`} data-testid={`format-${i}`}>
                <p className="font-mono-label text-[10px] opacity-70">{s.format} 0{i + 1}</p>
                <h3 className="font-display mt-6 text-2xl font-bold">{f.h}</h3>
                <ul className={`mt-5 space-y-2.5 text-[15px] ${i === 1 ? "text-white/85" : "text-[var(--ink-2)]"}`}>
                  {f.items.map((it) => <li key={it} className="flex gap-3"><Check size={16} className={`mt-1 shrink-0 ${i === 1 ? "text-[var(--lime)]" : "text-[var(--green)]"}`} />{it}</li>)}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Capabilities compact />

      <section className="py-20 lg:py-28" data-testid="certifications-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-6">
            <SectionHeader eyebrow={s.certEyebrow} title={s.certTitle} text={s.certText} />
          </div>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="rounded-3xl border border-[var(--line)] bg-white p-8 shadow-[0_30px_60px_-40px_rgba(14,61,34,0.35)]" data-testid="coa-card">
              <div className="flex items-center justify-between border-b border-[var(--line)] pb-5">
                <div className="flex items-center gap-3">
                  <FileCheck2 className="text-[var(--green)]" />
                  <p className="font-display text-lg font-semibold">{s.coa}</p>
                </div>
                <span className="font-mono-label text-[10px] text-[var(--muted)]">{s.sample}</span>
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-y-4 text-sm">
                {s.coaRows.map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-[var(--muted)]">{k}</dt>
                    <dd className="font-mono-label text-right text-[11px] text-[var(--green)]">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 text-xs leading-relaxed text-[var(--muted)]">{s.coaNote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
